import { DateTime, Settings } from "luxon";

// Export Settings so tests can modify timezone
export { Settings };

export const dateFormatType = {
  DEFAULT: "MMM dd, yyyy HH:mm",
  SHORT: "MMM dd, yyyy",
  LONG: "MMMM dd, yyyy HH:mm",
  TIME: "HH:mm",
} as const;

type DateFormatType = (typeof dateFormatType)[keyof typeof dateFormatType];

// dateString can be:
// 1. ISO format timestamp in UTC, for example: '2025-01-21T07:02:04.925769+00:00' / '2025-01-21T07:02:04.925769Z'
// 2. ISO format date only, for example: '2025-01-21'
// 3. SQL datetime format, for example: '2025-06-15 08:55:06'

export const getLocalDateTime = (dateString: string): DateTime => {
  const hasTimeInfo = /[T:]/.test(dateString);
  const isSqlFormat = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString);

  let localDateTime: DateTime;

  // if dateString is SQL format (YYYY-MM-DD HH:mm:ss) - treat as local
  if (isSqlFormat) {
    localDateTime = DateTime.fromSQL(dateString);
  }
  // if dateString is date only - treat as local
  else if (!hasTimeInfo) {
    localDateTime = DateTime.fromISO(dateString);
  }
  // else - convert to local
  else {
    const utcDateTime = DateTime.fromISO(dateString, { zone: "utc" });
    localDateTime = utcDateTime.toLocal();
  }

  return localDateTime;
};

export const getFormattedDate = (
  dateString?: string,
  formatType: DateFormatType = dateFormatType.DEFAULT
): string => {
  if (!dateString) {
    return "";
  }

  const localDateTime = getLocalDateTime(dateString);

  if (!localDateTime.isValid) {
    console.log(`Invalid date time: ${localDateTime}`);
    return "";
  }

  switch (formatType) {
    case dateFormatType.SHORT:
      return localDateTime.toFormat(dateFormatType.SHORT);
    case dateFormatType.LONG:
      return localDateTime.toFormat(dateFormatType.LONG);

    default:
      return localDateTime.toFormat(dateFormatType.DEFAULT);
  }
};

// timeString is whatever the Date constructor accepts; callers pass either a
// raw value or the Date | "" produced by parseToDateObj (see getFriendlyTimeFromNow).
export const getFormattedTime = (
  timeString: string | number | Date
): string => {
  if (!timeString) {
    console.log("Empty time string");
    return "";
  }

  const jsDate = new Date(timeString);
  const dateTime = DateTime.fromJSDate(jsDate);

  if (!dateTime.isValid) {
    console.log(`Invalid date time: ${dateTime}`);
    return "";
  }

  return dateTime.toFormat(dateFormatType.TIME);
};

export const getRelativeTimeFromNow = (
  dateString: string
): string | null | undefined => {
  if (!dateString) {
    return "";
  }

  const localDateTime = getLocalDateTime(dateString);
  if (!localDateTime.isValid) {
    return "";
  }

  const diffSeconds = localDateTime.diff(DateTime.now(), "seconds").seconds;
  if (Math.abs(diffSeconds) < 45) {
    return diffSeconds > 0 ? "in a few seconds" : "a few seconds ago";
  }

  return localDateTime.toRelative() ?? "";
};

export const getDaysFromNowDate = (days: number): DateTime =>
  DateTime.now().plus({ days });

export const parseToDateObj = (
  dateString: string
): Date | string | undefined => {
  if (!dateString) {
    return "";
  }

  const localDateTime = getLocalDateTime(dateString);
  if (!!localDateTime.isValid) {
    return localDateTime.toJSDate();
  }
};

// Strict variant of `parseToDateObj`: accepts `string | Date | null | undefined`
// and always returns `Date | null`.
//
// Also handles the space-separated SQL-ish format the `get_integration_statistics`
// resolver emits — `"2026-06-08 21:46:34.401259+00:00"` (space between date and
// time, microseconds, tz offset) — which Luxon's `fromISO` doesn't accept on its
// own. The space → "T" normalization here makes that payload parseable.
export const toValidDate = (
  value: string | Date | null | undefined
): Date | null => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const normalized = value.replace(/\s/, "T");
  const localDateTime = getLocalDateTime(normalized);
  if (!localDateTime.isValid) {
    return null;
  }

  const parsed = localDateTime.toJSDate();
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Date-only controls (e.g. shared DateInput) → yyyy-MM-dd local, or null.
export const parseDateInputToIsoDateString = (
  rawValue: string | null | undefined
): string | null => {
  if (rawValue == null || String(rawValue).trim() === "") {
    return null;
  }

  const localDateTime = getLocalDateTime(String(rawValue).trim());
  if (!localDateTime.isValid) {
    return null;
  }

  return localDateTime.toFormat("yyyy-MM-dd");
};

// Today's local date as yyyy-MM-dd — same shape/zone as parseDateInputToIsoDateString's output,
// for comparing a date-only control's value against "today".
export const getTodayIsoDateString = (): string =>
  DateTime.now().toFormat("yyyy-MM-dd");

const isInLast24Hrs = (dateString: string): boolean => {
  const localDateTime = getLocalDateTime(dateString);
  const now = DateTime.local();
  return localDateTime >= now.minus({ hours: 24 }) && localDateTime <= now;
};

export const getFriendlyTimeFromNow = (date: string): string => {
  if (!date) {
    return "N/A";
  }

  let updateDate: string;

  if (isInLast24Hrs(date)) {
    // Inside the isInLast24Hrs branch `date` parses to a valid DateTime, so
    // parseToDateObj returns a Date here (never undefined/""); assert to match.
    updateDate = `${getRelativeTimeFromNow(date)}, ${getFormattedTime(parseToDateObj(date)!)}`;
  } else {
    updateDate = getFormattedDate(date);
  }

  return updateDate;
};
